import { useState } from 'react';
import { Trash2, Pencil, Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useChatThreads } from '../../hooks/use-chat';
import type { ChatThreadSummary } from '../../types/chat';

function relativeTime(epochSecs: number): string {
  const diff = Math.floor(Date.now() / 1000) - epochSecs;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 7 * 86400) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(epochSecs * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

interface Props {
  activeThreadId: string | undefined;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onRename: (id: string, title: string) => Promise<void>;
}

export function ChatHistoryPanel({ activeThreadId, onSelect, onDelete, onRename }: Props) {
  const { data: threads, isLoading } = useChatThreads();
  const [query, setQuery] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');

  const startRename = (e: React.MouseEvent, t: ChatThreadSummary) => {
    e.stopPropagation();
    setEditingId(t.id);
    setEditDraft(t.title);
  };

  const cancelRename = (e?: React.KeyboardEvent | React.MouseEvent) => {
    e?.stopPropagation?.();
    setEditingId(null);
    setEditDraft('');
  };

  const commitRename = async (e?: React.KeyboardEvent | React.MouseEvent) => {
    e?.stopPropagation?.();
    if (!editingId) return;
    const trimmed = editDraft.trim();
    if (trimmed) {
      await onRename(editingId, trimmed);
    }
    setEditingId(null);
    setEditDraft('');
  };

  if (isLoading) {
    return (
      <div className="border-b p-3 text-xs text-muted-foreground animate-pulse">
        Loading history…
      </div>
    );
  }

  if (!threads || threads.length === 0) {
    return (
      <div className="border-b p-3 text-xs text-muted-foreground">
        No previous conversations.
      </div>
    );
  }

  const filtered = query.trim()
    ? threads.filter((t) => t.title.toLowerCase().includes(query.toLowerCase()))
    : threads;

  return (
    <div className="border-b">
      <div className="px-3 pt-2 pb-1">
        <Input
          placeholder="Search conversations…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="h-7 text-xs"
          aria-label="Search conversations"
        />
      </div>
      <div className="max-h-56 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="px-3 py-2 text-xs text-muted-foreground">
            No matching conversations.
          </div>
        ) : (
          filtered.map((t: ChatThreadSummary) => (
            <div
              key={t.id}
              className={`flex items-center gap-2 px-3 py-2 hover:bg-accent cursor-pointer group ${
                t.id === activeThreadId ? 'bg-accent/60' : ''
              }`}
            >
              {editingId === t.id ? (
                <div
                  className="flex flex-1 items-center gap-1 min-w-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Input
                    value={editDraft}
                    onChange={(e) => setEditDraft(e.target.value)}
                    className="h-6 flex-1 text-xs py-0"
                    aria-label="Rename conversation"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void commitRename(e);
                      if (e.key === 'Escape') cancelRename(e);
                    }}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0"
                    aria-label="Confirm rename"
                    onClick={(e) => void commitRename(e)}
                  >
                    <Check className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0"
                    aria-label="Cancel rename"
                    onClick={cancelRename}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <>
                  <button
                    className="flex-1 min-w-0 text-left"
                    onClick={() => onSelect(t.id)}
                    aria-label={`Load thread: ${t.title}`}
                  >
                    <span className="block truncate text-sm">{t.title}</span>
                    <span className="block text-xs text-muted-foreground">{relativeTime(t.updated_at)}</span>
                  </button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
                    onClick={(e) => startRename(e, t)}
                    aria-label={`Rename thread: ${t.title}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(t.id);
                    }}
                    aria-label={`Delete thread: ${t.title}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
